import React, { useState, useRef, useEffect } from "react";
import MessageBubble from "./MessageBubble";
import VoiceControls from "./VoiceControls";
import ExportChat from "./ExportChat";
import ImageUploader from "./ImageUploader";
import { sendMessage } from "../services/api";
import audioPlayer from "../services/audioPlayer";
import "./ChatInterface.css";

const ChatInterface = ({ sessionId, initialMessages, onSessionCreated }) => {
  const [messages, setMessages] = useState(initialMessages || []);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastSpokenRef = useRef("");

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setMessages(initialMessages || []); }, [initialMessages]);

  useEffect(() => {
    if (sessionId && initialMessages && initialMessages.length > 0) {
      import("../services/api").then(function(api) {
        api.getSession(sessionId).then(function(data) {
          if (data.conversation && data.conversation.messages) {
            setMessages(data.conversation.messages);
          }
        }).catch(function() {});
      });
    }
  }, [sessionId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { return () => audioPlayer.stop(); }, []);

  const speakOnce = (text) => {
    try {
      if (autoSpeak && text && text !== lastSpokenRef.current) {
        lastSpokenRef.current = text;
        audioPlayer.playText(text);
      }
    } catch(e) {
      console.log("Speak error (ignored):", e.message);
    }
  };

  const sendAndGetResponse = async (userMessage) => {
    setMessages((prev) => [...prev, { role: "user", content: userMessage, id: Date.now() + Math.random() }]);
    setIsLoading(true);
    try {
      const result = await sendMessage(userMessage, sessionId);
      if (result.sessionId && !sessionId) onSessionCreated(result.sessionId);
      
      const responseText = result.response || "No response received.";
      const newMessage = { role: "zanox", content: responseText, blocked: result.blocked || false, id: Date.now() + Math.random() };
      setMessages((prev) => [...prev, newMessage]);
      speakOnce(responseText);
      return newMessage;
    } catch (error) {
      console.log("Send error:", error.message);
      const errMsg = { role: "zanox", content: "Connection interrupted. Retry.", error: true, id: Date.now() + Math.random() };
      setMessages((prev) => [...prev, errMsg]);
      return errMsg;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async (zanoxMessage) => {
    const msgIndex = messages.findIndex((m) => m.id === zanoxMessage.id);
    if (msgIndex < 1) return;
    const userMsg = messages[msgIndex - 1];
    if (!userMsg || userMsg.role !== "user") return;
    setMessages((prev) => prev.filter((m) => m.id !== zanoxMessage.id));
    audioPlayer.stop();
    lastSpokenRef.current = "";
    setIsLoading(true);
    try {
      const result = await sendMessage(userMsg.content, sessionId);
      const responseText = result.response || "No response.";
      const newMessage = { role: "zanox", content: responseText, blocked: result.blocked || false, id: Date.now() + Math.random() };
      setMessages((prev) => [...prev, newMessage]);
      speakOnce(responseText);
    } catch (error) {
      setMessages((prev) => [...prev, { role: "zanox", content: "Retry failed.", error: true, id: Date.now() + Math.random() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendText = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput("");
    audioPlayer.stop();
    lastSpokenRef.current = "";
    await sendAndGetResponse(userMessage);
  };

  const handleVoiceTranscript = (transcript) => {
    if (!transcript.trim()) return;
    audioPlayer.stop();
    lastSpokenRef.current = "";
    sendAndGetResponse(transcript);
  };

  const handleImageAnalysis = (analysis) => {
    audioPlayer.stop();
    lastSpokenRef.current = "";
    setMessages((prev) => [...prev, { role: "zanox", content: analysis, imageAnalysis: true, id: Date.now() + Math.random() }]);
    speakOnce(analysis);
    setShowImageUploader(false);
  };

  return (
    <div className="chat-interface">
      <div className="messages-container">
        {messages.length === 0 && <div className="empty-chat"><i className="fas fa-infinity"></i><p>Begin your inquiry</p></div>}
        {messages.map((msg, index) => (
          <MessageBubble
            key={`${msg.id}-${index}`}
            message={msg}
            isLast={index === messages.length - 1 && msg.role === "zanox"}
            autoSpeak={autoSpeak}
            onRetry={msg.role === "zanox" ? () => handleRetry(msg) : null}
          />
        ))}
        {isLoading && <div className="typing-indicator"><span></span><span></span><span></span></div>}
        <div ref={messagesEndRef} />
      </div>

      {messages.length > 0 && <ExportChat messages={messages} sessionTitle="" />}
      {showImageUploader && <ImageUploader sessionId={sessionId} onAnalysis={handleImageAnalysis} onClose={() => setShowImageUploader(false)} />}

      <div className="input-area">
        <div className="input-toolbar">
          <button className={`toolbar-btn ${showImageUploader ? "active" : ""}`} onClick={() => setShowImageUploader(!showImageUploader)} title="Upload images">
            <i className="fas fa-image"></i>
          </button>
          <button className={`toolbar-btn ${autoSpeak ? "active" : ""}`} onClick={() => setAutoSpeak(!autoSpeak)} title={autoSpeak ? "Auto-speak ON" : "Auto-speak OFF"}>
            <i className={`fas ${autoSpeak ? "fa-volume-up" : "fa-volume-mute"}`}></i>
          </button>
        </div>
        <form onSubmit={handleSendText} className="input-form">
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask for truth..." className="text-input" disabled={isLoading} rows="1" onKeyDown={function(e) { if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 768) { e.preventDefault(); handleSendText(e); } }} />
          <button type="submit" className="send-btn" disabled={isLoading || !input.trim()}><i className="fas fa-arrow-right"></i></button>
        </form>
        <VoiceControls sessionId={sessionId} onTranscript={handleVoiceTranscript} disabled={isLoading} />
      </div>
    </div>
  );
};

export default ChatInterface;
